import { Box } from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { ptBR } from '@mui/x-data-grid/locales/ptBR';
import React, { ReactNode, useEffect, useState } from 'react';

import ThemeSplash from './components/common/effects/ThemeSplash';
import ThemeTransition from './components/common/transitions/ThemeTransition';
import { convertToTheme, DarkThemeOptions, LightThemeOptions } from './index';
import { ThemeContext } from './ThemeContext';

type ThemeProviderProps = {
     children: ReactNode;
};

export const ThemeProviderWrapper: React.FC<ThemeProviderProps> = ({ children }) => {
     // Inicializa com tema do sistema
     const [darkMode, setDarkMode] = useState<boolean>(() => {
          // Verifica se há preferência salva no localStorage
          const savedTheme = localStorage.getItem('theme-preference');
          if (savedTheme) {
               return savedTheme === 'dark';
          }
          // Se não há preferência salva, usa a preferência do sistema
          return window.matchMedia('(prefers-color-scheme: light)').matches;
     });

     const [isTransitioning, setIsTransitioning] = useState(false);
     const [showSplash, setShowSplash] = useState(false);

     const toggleTheme = () => {
          setIsTransitioning(true);
          setShowSplash(true);

          // Adiciona um pequeno delay para permitir animações
          setTimeout(() => {
               setDarkMode((prevMode) => {
                    const newMode = !prevMode;
                    // Salva a preferência do usuário
                    localStorage.setItem('theme-preference', newMode ? 'dark' : 'light');
                    return newMode;
               });
               setTimeout(() => setIsTransitioning(false), 300);
          }, 100);
     };

     const resetToSystemTheme = () => {
          setIsTransitioning(true);
          setShowSplash(true);

          setTimeout(() => {
               // Remove a preferência salva
               localStorage.removeItem('theme-preference');
               // Aplica o tema do sistema
               setDarkMode(window.matchMedia('(prefers-color-scheme: dark)').matches);
               setTimeout(() => setIsTransitioning(false), 300);
          }, 100);
     };

     const handleSplashComplete = () => {
          setShowSplash(false);
     };

     useEffect(() => {
          const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

          const handleThemeChange = (e: MediaQueryListEvent) => {
               // Só atualiza automaticamente se o usuário não definiu uma preferência específica
               const savedTheme = localStorage.getItem('theme-preference');
               if (!savedTheme) {
                    setDarkMode(e.matches);
               }
          };

          mediaQuery.addEventListener('change', handleThemeChange);

          return () => {
               mediaQuery.removeEventListener('change', handleThemeChange);
          };
     }, []);

     const selectedTheme = darkMode ? convertToTheme(DarkThemeOptions) : convertToTheme(LightThemeOptions);

     const themeWithLocale = createTheme(selectedTheme, ptBR);

     // Verifica se está usando tema do sistema
     const isSystemTheme = !localStorage.getItem('theme-preference');

     return (
          <ThemeContext.Provider
               value={{
                    theme: selectedTheme,
                    toggleTheme,
                    resetToSystemTheme,
                    isDarkMode: darkMode,
                    isSystemTheme,
               }}
          >
               <ThemeProvider theme={themeWithLocale}>
                    <Box
                         sx={{
                              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                              opacity: isTransitioning ? 0.95 : 1,
                              '& *': {
                                   transition: 'background-color 0.3s cubic-bezier(0.4, 0, 0.2, 1), color 0.3s cubic-bezier(0.4, 0, 0.2, 1), border-color 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important',
                              },
                         }}
                    >
                         {children}
                         <ThemeTransition
                              isTransitioning={isTransitioning}
                              isDarkMode={darkMode}
                         />
                         <ThemeSplash
                              show={showSplash}
                              isDarkMode={darkMode}
                              onComplete={handleSplashComplete}
                         />
                    </Box>
               </ThemeProvider>
          </ThemeContext.Provider>
     );
};
